import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Card, Text, Box, Checkbox, TextField
} from '@shopify/polaris';
import { formatMoney, API_BASE, resolvePlanIcon, planImage, isIconUrl } from '../constants';
import { useI18n } from '../i18n';
import { useAuth } from '../auth';

function calcPriceLocally(plan, subtotal) {
  // Mirrors the backend variant pricing: fixed tiers match in configured
  // order; percentage plans charge the first fee rung at or above pct% of
  // the subtotal (rungs: minCharge, minCharge+increment, …, 200 of them,
  // each variant priced at its rung).
  let price;
  if (plan.priceType === 'fixed') {
    price = plan.priceValue;
    const tiers = parseTiers(plan.pricingTiers);
    for (const t of tiers) {
      if (subtotal >= t.min && subtotal <= t.max) {
        price = t.price;
        break;
      }
    }
  } else {
    const min = numOr(plan.minCharge, 1.01);
    const inc = numOr(plan.incrementAmount, 2) || 2;
    const buckets = 200;
    const maxRung = min + inc * (buckets - 1);
    const target = (subtotal * (plan.priceValue || 0)) / 100;
    const rung = target <= min ? min : Math.min(maxRung, min + Math.ceil((target - min) / inc) * inc);
    price = rung;
  }
  if (plan.minPrice != null && price < plan.minPrice) price = plan.minPrice;
  if (plan.maxPrice != null && price > plan.maxPrice) price = plan.maxPrice;
  return Math.round(price * 100) / 100;
}

function numOr(v, fallback) {
  const n = parseFloat(v);
  return Number.isNaN(n) ? fallback : n;
}

function parseTiers(raw) {
  try {
    const arr = JSON.parse(raw || '[]');
    if (!Array.isArray(arr)) return [];
    return arr.map(t => ({
      min: numOr(t.min, 0),
      max: t.max === '' || t.max == null ? Infinity : numOr(t.max, Infinity),
      price: numOr(t.price, NaN)
    })).filter(t => !Number.isNaN(t.price));
  } catch {
    return [];
  }
}

function SkeletonBar({ width }) {
  return (
    <div
      aria-hidden="true"
      style={{ height: 10, borderRadius: 5, background: '#E4E4E4', width }}
    />
  );
}

export default function PreviewPanel({ plans, settings }) {
  const { shopDomain } = useAuth();
  const { t } = useI18n();
  // Saved+active plans always show; preview-only entries (__preview, e.g. the
  // plan being edited on its settings page) are appended by the parent.
  const visiblePlans = useMemo(
    () => plans.filter(p => p.isActive || p.__preview),
    [plans]
  );
  const [subtotal, setSubtotal] = useState('250');
  const [calculated, setCalculated] = useState({}); // planId -> calculatedPrice
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [calculating, setCalculating] = useState(false);
  const initializedRef = useRef(false);
  const subtotalNumber = parseFloat(subtotal) || 0;

  // Only these fields change what /cart/calculate returns per plan, so the
  // pricing request must refire only when one of them (or the subtotal)
  // changes — not on every keystroke elsewhere in the edit form.
  const pricingSignature = useMemo(
    () => visiblePlans
      .map(p => [p.id, p.priceType, p.priceValue, p.pricingTiers,
        p.minCharge, p.incrementAmount, p.minPrice, p.maxPrice].join('|'))
      .join(';'),
    [visiblePlans]
  );
  const plansRef = useRef(visiblePlans);
  plansRef.current = visiblePlans;

  // Default selection: auto-added plans are always in; default-enabled plans
  // start checked (mirrors the storefront pre-check); optionally preselect the
  // first optional plan (settings.autoSelectFirstPlan). Runs once per plan load.
  useEffect(() => {
    if (visiblePlans.length === 0) {
      setSelectedIds(new Set());
      initializedRef.current = false;
      return;
    }
    if (initializedRef.current) return;
    initializedRef.current = true;
    const next = new Set(
      visiblePlans.filter(p => !p.isOptional || p.defaultEnabled).map(p => p.id)
    );
    if (settings.autoSelectFirstPlan) {
      const first = visiblePlans.find(p => p.isOptional && !p.defaultEnabled);
      if (first) next.add(first.id);
    }
    setSelectedIds(next);
  }, [visiblePlans, settings.autoSelectFirstPlan]);

  // Recalculate via the real pricing API whenever the simulated subtotal changes;
  // fall back to the same formula locally if the API is unreachable.
  useEffect(() => {
    if (visiblePlans.length === 0) return;
    let cancelled = false;
    setCalculating(true);
    fetch(`${API_BASE}/api/cart/calculate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        shopDomain,
        subtotal: subtotalNumber,
        selectedPlanIds: [...selectedIds]
      })
    })
      .then(res => (res.ok ? res.json() : Promise.reject(new Error('calculate failed'))))
      .then(data => {
        if (cancelled) return;
        const next = {};
        data.plans.forEach(p => { next[p.id] = p.calculatedPrice; });
        setCalculated(next);
      })
      .catch(() => {
        if (cancelled) return;
        const next = {};
        plansRef.current.forEach(p => { next[p.id] = calcPriceLocally(p, subtotalNumber); });
        setCalculated(next);
      })
      .finally(() => { if (!cancelled) setCalculating(false); });
    return () => { cancelled = true; };
  }, [subtotalNumber, pricingSignature, shopDomain]);

  const priceFor = (plan) => calculated[plan.id] ?? calcPriceLocally(plan, subtotalNumber);

  const togglePlan = (plan, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(plan.id); else next.delete(plan.id);
      return next;
    });
  };

  const selectedTotal = visiblePlans
    .filter(p => !p.isOptional || selectedIds.has(p.id))
    .reduce((sum, p) => sum + priceFor(p), 0);

  return (
    <Card padding="0">
      {/* Controls */}
      <div style={{ padding: '12px 14px', borderBottom: '1px solid #E3E3E3' }}>
        <Text variant="headingSm" as="h2">{t('preview.title')}</Text>
        <Box paddingBlockStart="200">
          <TextField
            label={t('preview.simulatedSubtotal')}
            labelHidden
            placeholder={t('preview.simulatedSubtotal')}
            type="number"
            min={0}
            step={1}
            prefix="$"
            value={subtotal}
            onChange={setSubtotal}
            autoComplete="off"
          />
        </Box>
      </div>

      {/* Cart mock — edge to edge, no inner padding on the card */}
      <div style={{ padding: '14px' }}>
        <Text variant="headingLg" as="p" fontWeight="bold">{t('preview.cart')}</Text>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 10,
            fontSize: 11,
            letterSpacing: '0.04em',
            color: '#8A8A8A'
          }}
        >
          <span>{t('preview.product')}</span>
          <span>{t('preview.total')}</span>
        </div>

        {/* product skeleton row */}
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            padding: '10px 0',
            borderBottom: '1px solid #ECECEC'
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #EFEFEF, #DCDCDC)',
              flexShrink: 0
            }}
          />
          <div style={{ flex: 1 }}>
            <SkeletonBar width="80%" />
            <div style={{ height: 7 }} />
            <SkeletonBar width="38%" />
          </div>
          <span style={{ fontSize: 13, color: '#1A1A1A' }}>
            {formatMoney(subtotalNumber)}
          </span>
        </div>

        {/* widget title */}
        {visiblePlans.length > 0 && (
          <div style={{ padding: '10px 0 2px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A' }}>
              {settings.widgetTitle}
            </div>
          </div>
        )}

        {/* plan widgets (checkboxes kept) */}
        {visiblePlans.length === 0 ? (
          <div style={{ padding: '10px 0', fontSize: 12, color: '#8A8A8A' }}>
            {t('preview.noPlans')}
          </div>
        ) : (
          visiblePlans.map((plan) => {
            const isAuto = !plan.isOptional;
            const checked = isAuto || selectedIds.has(plan.id);
            const price = priceFor(plan);
            const icon = settings.showIcons
              ? (isIconUrl(plan.icon) ? '' : (resolvePlanIcon(plan.icon) || '🛡️'))
              : null;
            const iconImg = settings.showIcons ? planImage(plan.icon) : null;
            // 勾选 → enabled 描述;未勾选 → disabled 描述(未填则回退 enabled 描述)
            const description = settings.showDescriptions
              ? (checked
                ? (plan.displayDescription || plan.description)
                : (plan.disabledDescription || plan.displayDescription || plan.description))
              : null;
            return (
              <div
                key={plan.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '10px 0',
                  borderBottom: '1px solid #ECECEC'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 0 }}>
                  {icon && (
                    <div
                      aria-hidden="true"
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: settings.accentColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                        flexShrink: 0
                      }}
                    >
                      {iconImg ? (
                        <img
                          src={iconImg}
                          alt={icon || ''}
                          width="20"
                          height="20"
                          style={{ display: 'block' }}
                        />
                      ) : (
                        icon
                      )}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#1A1A1A' }}>
                        {plan.displayName || plan.name || t('common.untitledPlan')}
                      </span>
                      <span style={{ fontSize: 13, color: '#1A1A1A' }}>
                        ({calculating ? '…' : formatMoney(price)})
                      </span>
                      {isAuto && (
                        <span style={{ fontSize: 11, color: '#8A8A8A' }}>{t('preview.autoAdded')}</span>
                      )}
                      {plan.__dirty && (
                        <span
                          style={{
                            fontSize: 11,
                            color: '#9C6A00',
                            background: '#FFF3D6',
                            borderRadius: 6,
                            padding: '1px 6px'
                          }}
                        >
                          {t('preview.unsaved')}
                        </span>
                      )}
                    </div>
                    {description && (
                      <p
                        style={{
                          margin: '3px 0 0',
                          fontSize: 12,
                          lineHeight: 1.45,
                          color: '#616161',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {description}
                      </p>
                    )}
                    {plan.footerEnabled !== false && (
                      <span
                        style={{
                          display: 'inline-block',
                          marginTop: 4,
                          fontSize: 12,
                          color: '#616161',
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                      >
                        {plan.footerText || t('common.viewTerms')}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ flexShrink: 0, paddingTop: 2 }}>
                  <Checkbox
                    label=""
                    labelHidden
                    checked={checked}
                    disabled={isAuto}
                    onChange={(value) => togglePlan(plan, value)}
                  />
                </div>
              </div>
            );
          })
        )}

        {/* subtotal */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 0'
          }}
        >
          <span style={{ fontSize: 13, color: '#1A1A1A' }}>{t('preview.subtotal')}</span>
          <span style={{ fontSize: 13, color: '#1A1A1A' }}>
            {calculating ? '…' : formatMoney(subtotalNumber + selectedTotal)}
          </span>
        </div>

        {/* checkout button (decorative) */}
        <div
          aria-hidden="true"
          style={{
            marginTop: 10,
            textAlign: 'center',
            background: '#EBEBEB',
            borderRadius: 8,
            padding: '9px 0',
            fontSize: 13,
            fontWeight: 500,
            color: '#303030'
          }}
        >
          {t('preview.checkout')}
        </div>
      </div>
    </Card>
  );
}
